<script lang="ts">
  import type { HTMLButtonAttributes } from "svelte/elements";

  import TextNew, { type TextSize } from "../TextNew.svelte";
  import LoadingSpinner from "../LoadingSpinner.svelte";

  type ButtonVariant = "primary" | "secondary" | "icon";
  type ElementType = "a" | "button";
  interface $$Props extends HTMLButtonAttributes {
    title?: string;
    message?: string;
    variant?: ButtonVariant;
    size?: ButtonSize;
    textSize?: TextSize;
    border?: boolean;
    as?: ElementType;
    href?: string | undefined;
    element?: HTMLElement | null;
    isLoading?: boolean;
    loadingSize?: number;
    fullWidth?: boolean;
  }

  /**
   * no definitive sizes yet, but we can start with naming these according to font size.
   * This way, the button will scale according to text dimensions + whatever padding we decide to add.
   * (13px 20px padding for all currently, but we can change this if necessary for each variant)
   */
  type ButtonSize = "xxs" | "xs" | "s" | "m" | "l";

  // export let loading = false;
  export let variant: ButtonVariant = "primary";
  export let size: ButtonSize = "s";
  export let textSize: TextSize = size;
  export let border: boolean = false;
  export let as: ElementType = "button";
  export let href: string | undefined = undefined;
  export let element: HTMLElement | null = null;
  export let isLoading: boolean = false;
  export let loadingSize: number = 14;
  export let fullWidth: boolean = false;

  const textColorsByVariant = {
    primary: "var(--rk-text-light)",
    secondary: "var(--rk-text-dark)",
  };
</script>

<svelte:element
  this={as}
  {...$$restProps}
  {href}
  class={`${variant === "icon" ? "" : size} ${variant === "icon" ? "icon-btn" : variant}`}
  class:border
  class:fullwidth={fullWidth}
  on:click
  on:mouseover
  on:mouseleave
  on:focus
  bind:this={element}
>
  {#if isLoading}
    <LoadingSpinner light size={loadingSize} />
  {:else}
    {#if $$slots.icon}
      <div
        class="icon"
        class:icon-l={variant !== "icon"}
        class:icon-m={variant !== "icon"}
        class:icon-s={variant !== "icon"}
        class:icon-xs={variant !== "icon"}
        class:icon-solo={variant === "icon"}
      >
        <slot name="icon" />
      </div>
    {/if}

    {#if variant !== "icon"}
      <div class="content">
        <TextNew
          as="span"
          size={textSize}
          weight="medium"
          --rk-text-color={textColorsByVariant[variant]}
          --text-display="block"
        >
          <slot />
        </TextNew>
        <slot name="rightAdornment" />
      </div>
    {/if}
  {/if}
</svelte:element>

<style>
  button {
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 666px;
    padding: 13px 20px;
    min-width: var(--minW);
  }

  .content {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .border {
    border: 1px solid var(--rk-divider-0);
  }

  .icon {
    display: flex;
  }

  .icon-l {
    height: 24px;
    width: 24px;
    margin-right: 8px;
  }
  .icon-m {
    height: 24px;
    width: 24px;
    margin-right: 8px;
  }
  .icon-s {
    height: 20px;
    width: 20px;
    margin-right: 8px;
  }
  .icon-xs {
    height: 16px;
    width: 16px;
    margin-right: 6px;
  }

  .icon-solo {
    margin-right: 0px;
  }

  .xxs {
    padding: 7px 12px;
  }

  .xs {
    padding: 12px 16px;
  }

  .s {
    padding: 13px 20px;
  }
  .m {
    padding: 20px 32px;
  }
  .l {
    flex: 1;
    border-radius: 8px;
    max-height: 80px;
    padding: 28px 32px;
  }

  .primary {
    background-color: var(--bg, var(--rekki-accent));
  }

  .secondary {
    background-color: var(--bg, var(--rekki-light));
  }

  .icon-btn {
    background-color: transparent;
    padding-left: var(--icon-btn-pl, 8px);
    padding-right: var(--icon-btn-pr, 8px);
    padding-top: var(--icon-btn-pt, 8px);
    padding-bottom: var(--icon-btn-pb, 8px);
    color: var(--icon-color);
    height: var(--icon-btn-height);
  }

  .icon-btn:hover {
    color: var(--icon-color-hover);
  }

  .icon-btn:focus-visible {
    outline: 2px solid var(--rekki-accent);
  }

  .icon-btn:disabled {
    color: var(--rk-neutral-1);
  }

  button:enabled:active {
    filter: brightness(1.1);
  }

  .fullwidth {
    width: 100%;
  }
</style>
